import { EMessageResponseTypes, PopupManager, PushManager } from '@cian/push-manager';
import { ISaveSearchProps } from '@cian/save-search-widget';
import React, { useRef, useState, FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IAppState } from '../../../common/state';
import { IJsonQuery } from '../../../json_query';
import { TOnSubscribedExtendedParameters } from '../../../types/savedSearch';
import { TUser } from '../../../types/user';
import { useApplicationContext } from '../../../utils/applicationContext';
import { ISaveSearchModalProps, SaveSearchModal } from '../../components/SaveSearchModal';
import { trackOpen } from './tracking';

type TSaveSearchModalContainerProps = Pick<ISaveSearchModalProps, 'open' | 'onClose'> & {
  onSubscribed?: (parameters: TOnSubscribedExtendedParameters) => void;
};

export const SaveSearchModalContainer: FC<TSaveSearchModalContainerProps> = sharedProps => {
  const { open: isOpen, onSubscribed } = sharedProps;
  const { config, logger, httpApi } = useApplicationContext();
  const pushBrowserUrl = config.get('push.browserUrl') as string;
  const applicationServerKey = config.get<string>('applicationServerKey') || '';
  const pushManager = useRef<PushManager>();
  const currentSubdomain = useSelector<IAppState, string>(state => state.currentSubdomain);
  const jsonQuery = useSelector<IAppState, IJsonQuery>(state => state.filters.jsonQuery);
  const title = useSelector<IAppState, string | null>(state => state.results.seo.h1);
  const user = useSelector<IAppState, TUser>(state => state.user);
  const email = useMemo(() => (user.isAuthenticated ? user.email : null), [user]);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const subscribeToPushAvailable = useMemo(
    () => !isSubscribed && pushPermission !== 'denied',
    [isSubscribed, pushPermission],
  );

  const handlePushSubscribe = useCallback(async () => {
    if (currentSubdomain !== 'www') {
      if (pushManager.current) {
        pushManager.current.showPopup({
          baseUrl: 'web-push-subscribe-popup',
          trackingLabel: 'From_cat_save',
        });
      }
    } else if (applicationServerKey) {
      const popup = new PopupManager({
        workerSrc: '/push-worker.js',
        applicationServerKey,
        httpApi,
      });

      try {
        await popup.register();
      } catch (error) {
        logger.warning(error, { domain: 'shared.filters.containers.SaveSearchModalContainer.PopupManager.register' });

        return;
      }

      try {
        await popup.subscribe();
      } catch (error) {
        logger.warning(error, { domain: 'shared.filters.containers.SaveSearchModalContainer.PopupManager.subscribe' });

        return;
      }
    }
  }, [applicationServerKey, currentSubdomain, httpApi, logger]);

  const handleSubscribed = useCallback(
    (parameters: Parameters<Required<ISaveSearchProps>['onSubscribed']>[0]) => {
      if (onSubscribed) {
        onSubscribed({
          subscribeToPushAvailable,
          defaultTitle: title,
          defaultEmail: email,
          ...parameters,
        });
      }

      if (window.__reloadHeader__) {
        window.__reloadHeader__();
      }
    },
    [email, onSubscribed, subscribeToPushAvailable, title],
  );

  const handlePushManagerMessage = useCallback((e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);

      if (data.type === EMessageResponseTypes.subscriptionStatus && !data.value) {
        setIsSubscribed(data.value);
      }
      // eslint-disable-next-line no-empty
    } catch {}
  }, []);

  useEffect(() => {
    if (isOpen) {
      pushManager.current = new PushManager({
        baseUrl: pushBrowserUrl,
        hasSubdomain: currentSubdomain !== 'www',
      });

      pushManager.current.registerWorker();
      PushManager.ready().then(PushManager.checkSubscription);

      if ('Notification' in window && currentSubdomain === 'www') {
        setPushPermission(Notification.permission);
      }

      window.addEventListener('message', handlePushManagerMessage);

      trackOpen();
    }

    return () => window.removeEventListener('message', handlePushManagerMessage);
  }, [isOpen]);

  return (
    <SaveSearchModal
      {...sharedProps}
      httpApi={httpApi}
      logger={logger}
      subdomain={currentSubdomain}
      jsonQuery={jsonQuery}
      title={title}
      hideLicense={user.isAuthenticated}
      userEmail={email}
      subscribeToPushAvailable={subscribeToPushAvailable}
      onPushSubscribe={handlePushSubscribe}
      onSubscribed={handleSubscribed}
    />
  );
};
